import React, { useMemo, useState } from 'react';
import { Box, MenuItem, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { withBrandName } from '../../../config/appConfig';
import FeatureNotSupportedDialog from '../../common/FeatureNotSupportedDialog';
import { useGetKpisHandler } from '../dataHandlers/getKpisHandler';
import { useGetResultsHandler } from '../dataHandlers/getResultsHandler';
import { useGetUploadsHandler } from '../dataHandlers/getUploadsHandler';
import { useGetFiltersHandler } from '../dataHandlers/getFiltersHandler';
import UploadDropzone from '../components/UploadDropzone';
import UploadSessionsTable from '../components/UploadSessionsTable';
import ReconKpiCards from '../components/ReconKpiCards';
import ClassificationPills from '../components/ClassificationPills';
import ResultsTable from '../components/ResultsTable';
import ExportExcelButton from '../components/ExportExcelButton';
import ReconPeriodFilter from '../components/filters/ReconPeriodFilter';
import { CLASSIFICATIONS, RECON_UI } from '../utils/reconConstants';
import { PERIOD_PRESETS, getPeriodRange } from '../utils/periodPresets';
import { formatReconTimestampLocal } from '../utils/formatters';
import { LANGUAGES } from '../../../constants/appConstants';

const TABS = { UPLOADS: 'uploads', AR: 'ar', AP: 'ap' };

const DEFAULT_FILTERS = {
  periodPreset: PERIOD_PRESETS.THIS_MONTH,
  customStart: null,
  customEnd: null,
  tin: '',
};

// Uploads = dropzone + batch log. Sales (AR) and Purchases (AP) share the
// reporting body, filtered by ledger.
export default function ReconciliationPage() {
  const { reconciliation: r, language } = useTranslatedMessages();
  useDocumentTitle(r.page_title ?? 'Document Reconciliation');
  const isReconciliationEnabled = Boolean(
    useSelector((state) => state.subscription.subscription?.features?.enable_document_reconciliation),
  );

  const [tab, setTab] = useState(TABS.UPLOADS);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [classification, setClassification] = useState(CLASSIFICATIONS.PERFECT_MATCH);
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const ledger = tab === TABS.AP ? 'ap' : tab === TABS.AR ? 'ar' : undefined;

  const apiFilters = useMemo(() => {
    const params = {};
    if (ledger) params.ledger = ledger;
    if (filters.periodPreset === PERIOD_PRESETS.CUSTOM) {
      if (filters.customStart && filters.customEnd) {
        params.date_from = filters.customStart;
        params.date_to = filters.customEnd;
      }
    } else {
      const range = getPeriodRange(filters.periodPreset);
      if (range) {
        params.date_from = range.start.format('YYYY-MM-DD');
        params.date_to = range.end.format('YYYY-MM-DD');
      }
    }
    if (filters.tin) params.tin = filters.tin;
    return params;
  }, [filters, ledger]);

  const { kpis } = useGetKpisHandler({ queryParams: apiFilters, enabled: isReconciliationEnabled });
  const { tins } = useGetFiltersHandler({ ledger, enabled: isReconciliationEnabled });
  const {
    sessions,
    totalSessions,
    isLoading: sessionsLoading,
  } = useGetUploadsHandler({
    page: 0,
    pageSize: 20,
    enabled: isReconciliationEnabled,
  });
  const {
    results,
    totalResults,
    isLoading: resultsLoading,
  } = useGetResultsHandler({
    classification,
    filters: apiFilters,
    page,
    pageSize,
    enabled: isReconciliationEnabled && Boolean(ledger),
  });

  const setFilter = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(0);
  };

  const ft = r.filters ?? {};
  const timestampLocale = language === LANGUAGES.AR ? 'ar' : language === LANGUAGES.ZH ? 'zh-CN' : 'en';

  if (!isReconciliationEnabled) {
    return (
      <FeatureNotSupportedDialog
        title={r.feature_not_supported_title ?? r.page_title ?? 'Document Reconciliation'}
        description={withBrandName(
          r.feature_not_supported_description ?? 'Document reconciliation is not available on this subscription.',
        )}
      />
    );
  }

  return (
    <Box sx={{ px: { xs: 1, md: 3 }, py: 3, maxWidth: 1600, mx: 'auto', width: '100%' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={600} sx={{ letterSpacing: -0.3 }}>
            {r.page_title ?? 'Document Reconciliation'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {withBrandName(r.page_subtitle ?? '')}
          </Typography>
        </Box>
        {kpis?.snapshot?.computed_at && (
          <Typography variant="caption" color="text.secondary">
            {r.run?.last_updated ?? 'Last updated'}:{' '}
            {formatReconTimestampLocal(kpis.snapshot.computed_at, timestampLocale)}
          </Typography>
        )}
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, next) => {
          setTab(next);
          setPage(0);
        }}
        textColor="inherit"
        variant="fullWidth"
        sx={{
          mt: 3,
          maxWidth: 780,
          bgcolor: RECON_UI.SURFACE_MUTED,
          borderRadius: 1.5,
          minHeight: 38,
          p: 0.5,
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTab-root': {
            minHeight: 30,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 13,
            borderRadius: 1,
            color: RECON_UI.TAB_TEXT,
          },
          '& .Mui-selected': {
            bgcolor: RECON_UI.SURFACE_WHITE,
            color: RECON_UI.TAB_TEXT_SELECTED,
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
          },
        }}
      >
        <Tab value={TABS.UPLOADS} label={r.tabs?.uploads ?? 'ERP Data Upload'} />
        <Tab value={TABS.AR} label={r.tabs?.ar ?? 'Sales (AR) Reporting'} />
        <Tab value={TABS.AP} label={r.tabs?.ap ?? 'Purchases (AP) Reporting'} />
      </Tabs>

      {tab === TABS.UPLOADS && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <UploadDropzone />
          <UploadSessionsTable
            sessions={sessions}
            loading={sessionsLoading}
            totalSessions={totalSessions}
            onGoToReporting={() => setTab(TABS.AR)}
          />
        </Stack>
      )}

      {(tab === TABS.AR || tab === TABS.AP) && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <ReconKpiCards kpis={kpis} ledger={ledger} />

          <Box
            sx={{
              py: 1,
              borderTop: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ overflowX: 'auto', pb: 0.5 }}>
              <ReconPeriodFilter
                periodPreset={filters.periodPreset}
                startDate={
                  filters.periodPreset === PERIOD_PRESETS.CUSTOM
                    ? filters.customStart
                    : getPeriodRange(filters.periodPreset)?.start.format('YYYY-MM-DD')
                }
                endDate={
                  filters.periodPreset === PERIOD_PRESETS.CUSTOM
                    ? filters.customEnd
                    : getPeriodRange(filters.periodPreset)?.end.format('YYYY-MM-DD')
                }
                onChange={({ periodPreset, startDate, endDate }) =>
                  setFilter({ periodPreset, customStart: startDate, customEnd: endDate })
                }
              />

              <TextField
                select
                size="small"
                value={filters.tin}
                onChange={(e) => setFilter({ tin: e.target.value })}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => (value ? value : (ft.all_entities ?? 'All Entities')),
                }}
                sx={{ minWidth: 220, flexShrink: 0, '& .MuiInputBase-root': { height: 34, fontSize: 13 } }}
              >
                <MenuItem value="" sx={{ fontSize: 13 }}>
                  {ft.all_entities ?? 'All Entities'}
                </MenuItem>
                {tins.map((tin) => (
                  <MenuItem key={tin} value={tin} sx={{ fontSize: 13 }}>
                    {tin}
                  </MenuItem>
                ))}
              </TextField>

              <Box sx={{ ml: 'auto !important', display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                <ClassificationPills
                  kpis={kpis}
                  selected={classification}
                  onSelect={(next) => {
                    setClassification(next);
                    setPage(0);
                  }}
                />
                <ExportExcelButton classification={classification} filters={apiFilters} disabled={resultsLoading} />
              </Box>
            </Stack>
          </Box>

          <ResultsTable
            results={results}
            loading={resultsLoading}
            totalResults={totalResults}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            ledger={ledger}
          />
        </Stack>
      )}
    </Box>
  );
}
